<template>
  <div :style="style" ref="lavContainer"></div>
</template>

<script>
  import lottie from "lottie-web";

  export default {
    props: {
      options: {
        type: Object,
        required: true
      },
      height: Number,
      width: Number
    },

    data() {
      return {
        style: {
          width: this.width ? `${this.width}px` : "100%",
          height: this.height ? `${this.height}px` : "100%",
          overflow: "hidden",
          margin: "0 auto"
        }
      };
    },
    methods: {
      // Lottie Creator exports need a few fixes for lottie-web text rendering.
      normalizeAnimationData(animationData) {
        if (!animationData) return animationData;

        const fonts = (animationData.fonts && animationData.fonts.list) || [];
        const needsFontFix = fonts.some(
          font => font.fPath && (font.origin === 0 || font.origin === "0")
        );
        const needsAnimatorFix = this.hasBrokenTextAnimators(animationData);
        const needsNewlineFix = this.hasLfNewlines(animationData);
        if (!needsFontFix && !needsAnimatorFix && !needsNewlineFix) {
          return animationData;
        }

        const data = JSON.parse(JSON.stringify(animationData));
        if (needsFontFix) {
          data.fonts.list.forEach(font => {
            if (font.fPath && (font.origin === 0 || font.origin === "0")) {
              font.origin = 3;
              font.fOrigin = "p";
            }
          });
        }
        if (needsAnimatorFix) {
          this.stripBrokenTextAnimators(data.layers);
          (data.assets || []).forEach(asset => {
            this.stripBrokenTextAnimators(asset.layers);
          });
        }
        if (needsNewlineFix) {
          this.fixTextNewlines(data.layers);
          (data.assets || []).forEach(asset => {
            this.fixTextNewlines(asset.layers);
          });
        }
        return data;
      },
      hasBrokenTextAnimators(data) {
        const check = layers =>
          (layers || []).some(layer => {
            if (layer.ty === 5 && this.isBrokenTextAnimator(layer)) return true;
            if (layer.layers && check(layer.layers)) return true;
            return false;
          });
        if (check(data.layers)) return true;
        return (data.assets || []).some(asset => check(asset.layers));
      },
      isBrokenTextAnimator(layer) {
        const animators = (layer.t && layer.t.a) || [];
        return animators.some(animator => {
          const fb = animator.a && animator.a.fb;
          const fs = animator.a && animator.a.fs;
          // Creator default fs/fb:100 are HSV offsets in lottie-web → white text
          return (
            (fb && !fb.a && Number(fb.k) === 100) ||
            (fs && !fs.a && Number(fs.k) === 100)
          );
        });
      },
      stripBrokenTextAnimators(layers) {
        (layers || []).forEach(layer => {
          if (layer.ty === 5 && this.isBrokenTextAnimator(layer)) {
            layer.t.a = [];
          }
          if (layer.layers) this.stripBrokenTextAnimators(layer.layers);
        });
      },
      hasLfNewlines(data) {
        const check = layers =>
          (layers || []).some(layer => {
            if (layer.ty === 5) {
              const frames = (layer.t && layer.t.d && layer.t.d.k) || [];
              if (
                frames.some(
                  frame => frame.s && typeof frame.s.t === "string" && frame.s.t.includes("\n")
                )
              ) {
                return true;
              }
            }
            return layer.layers ? check(layer.layers) : false;
          });
        if (check(data.layers)) return true;
        return (data.assets || []).some(asset => check(asset.layers));
      },
      // lottie-web only treats \r (and char 3) as line breaks, not \n
      fixTextNewlines(layers) {
        (layers || []).forEach(layer => {
          if (layer.ty === 5) {
            const frames = (layer.t && layer.t.d && layer.t.d.k) || [];
            frames.forEach(frame => {
              if (!frame.s || typeof frame.s.t !== "string") return;
              if (frame.s.t.includes("\n")) {
                frame.s.t = frame.s.t.replace(/\n/g, "\r");
              }
              if (!frame.s.lh && frame.s.s) {
                frame.s.lh = Math.round(frame.s.s * 1.2 * 100) / 100;
              }
            });
          }
          if (layer.layers) this.fixTextNewlines(layer.layers);
        });
      }
    },
    mounted() {
      this.anim = lottie.loadAnimation({
        container: this.$refs.lavContainer,
        renderer: "svg",
        loop: this.options.loop !== false,
        autoplay: this.options.autoplay !== false,
        animationData: this.normalizeAnimationData(this.options.animationData),
        rendererSettings: this.options.rendererSettings
      });
      this.$emit("animCreated", this.anim);
    }
  };
</script>
